import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BypassLayout, DesktopPagination } from '../components/common';
import TeacherModal from '../components/TeacherManagement/TeacherModal';
import ManagerModal from '../components/ManagerManagement/ManagerModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { PenSquare, Trash2, Users, UserCheck } from 'lucide-react';
import { Button, ConfirmModal } from '../components/common';

function UserManagement() {
  const [activeTab, setActiveTab] = useState('teachers');
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  
  // 강사 관련 상태
  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [teacherModalMode, setTeacherModalMode] = useState('add');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherPageInputValue, setTeacherPageInputValue] = useState('1');
  const [teacherTotalPages, setTeacherTotalPages] = useState(1);
  const [teacherHasMore, setTeacherHasMore] = useState(true);
  const [teacherDeleteConfirm, setTeacherDeleteConfirm] = useState({ isOpen: false, userId: null });

  // 관리자 관련 상태
  const [managerModalOpen, setManagerModalOpen] = useState(false);
  const [managerModalMode, setManagerModalMode] = useState('add');
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managerPage, setManagerPage] = useState(1);
  const [managerPageInputValue, setManagerPageInputValue] = useState('1');
  const [managerTotalPages, setManagerTotalPages] = useState(1);
  const [managerHasMore, setManagerHasMore] = useState(true);
  const [managerDeleteConfirm, setManagerDeleteConfirm] = useState({ isOpen: false, userId: null });
  const tableContainerRef = useRef(null);

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = useCallback(() => {
    try {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // 실제 모바일 기기인지 빠르게 체크
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileView = windowWidth < 768;
      const isDesktopResponsive = windowWidth < 768 && !isMobileDevice && windowWidth >= 375;
      
      let fixedHeight, rowHeight;
      
      if (isDesktopResponsive) {
        // 데스크톱에서 모바일 반응형으로 전환된 경우
        // 탭 헤더(48px) + 헤더 영역(76px) + 테이블 헤더(35px) + 페이지네이션(56px) + 패딩/마진(20px)
        fixedHeight = 48 + 76 + 35 + 56 + 20;
        rowHeight = 48;
      } else if (isMobileView) {
        // 실제 모바일 기기
        // BypassLayout 상단(env(safe-area-inset-top)) + 탭 헤더(48px) + 헤더 영역(88px) + 테이블 헤더(40px) + 페이지네이션(72px) + 컨텐츠 패딩(24px)
        fixedHeight = 48 + 88 + 40 + 72 + 24; // 272px (여유 공간 제거)
        rowHeight = 52;
      } else {
        // 데스크톱
        // 네비게이션(0) + BypassLayout 마진(32px) + 탭 헤더(48px) + 헤더 영역(88px) + 테이블 헤더(40px) + 페이지네이션(68px) + 컨텐츠 패딩(32px)
        fixedHeight = 32 + 48 + 88 + 40 + 68 + 32; // 308px (여유 공간 제거)
        rowHeight = 48;
      }
      
      const availableHeight = windowHeight - fixedHeight;
      const itemsCount = Math.floor(availableHeight / rowHeight);
      
      console.log(`${isDesktopResponsive ? 'Desktop-Responsive' : (isMobileView ? 'Mobile' : 'Desktop')} UserManagement calculation:`, {
        windowHeight,
        windowWidth,
        fixedHeight, 
        availableHeight, 
        rowHeight,
        itemsCount,
        isMobileDevice,
        isDesktopResponsive
      });
      
      // 세로 스크롤이 생기지 않도록 계산된 값 그대로 사용
      // 최소값은 1로 설정하여 항상 최소 1개는 표시
      const minItems = 1;
      const maxItems = isMobileView ? 30 : 50;
      
      // 계산된 itemsCount가 1보다 작으면 1을 반환
      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 10; // 기본값
    }
  }, []);

  // 강사 관련 함수들
  const fetchTeachers = async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const itemsPerPage = calculateItemsPerPage();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/teacher?page=${pageNum}&per_page=${itemsPerPage}`, {
        headers: {
          'Authorization': token
        }
      });
      const data = await response.json();
      
      if (data.status === 'error') {
        setTeachers([]);
      } else {
        setTeachers(data.data);
        setTeacherTotalPages(data.total_pages || 1);
        setTeacherHasMore(pageNum < data.total_pages);
        setTeacherPage(pageNum);
      }
    } catch (error) {
      console.error('강사 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTeacher = async (teacherData) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/teacher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(teacherData),
      });

      if (!response.ok) throw new Error('강사 추가 실패');
      
      await fetchTeachers();
      return '강사가 성공적으로 추가되었습니다.';
    } catch (error) {
      throw new Error('강사 추가에 실패했습니다.');
    }
  };

  const handleEditTeacher = async (teacherData) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/teacher`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(teacherData),
      });

      if (!response.ok) throw new Error('강사 정보 수정 실패');
      
      await fetchTeachers();
      return '강사 정보가 성공적으로 수정되었습니다.';
    } catch (error) {
      throw new Error('강사 정보 수정에 실패했습니다.');
    }
  };

  const handleDeleteTeacher = (userId) => {
    setTeacherDeleteConfirm({ isOpen: true, userId });
  };

  const confirmTeacherDelete = async () => {
    const { userId } = teacherDeleteConfirm;
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/teacher`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) throw new Error('강사 삭제 실패');
      
      await fetchTeachers();
    } catch (error) {
      console.error('강사 삭제 실패:', error);
    } finally {
      setTeacherDeleteConfirm({ isOpen: false, userId: null });
    }
  };

  const handleTeacherModalOpen = (mode, teacher = null) => {
    setTeacherModalMode(mode);
    if (mode === 'edit' && teacher) {
      setSelectedTeacher({
        id: teacher.id,
        user_id: teacher.user_id,
        user_name: teacher.user_name,
        charge_work: teacher.charge_work
      });
    }
    setTeacherModalOpen(true);
  };

  const handleTeacherModalClose = () => {
    setTeacherModalOpen(false);
    setSelectedTeacher(null);
    setTeacherModalMode('add');
  };

  const handleTeacherPageChange = (newPage) => {
    fetchTeachers(newPage);
  };

  // 관리자 관련 함수들
  const fetchManagers = async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const itemsPerPage = calculateItemsPerPage();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager?page=${pageNum}&per_page=${itemsPerPage}`, {
        headers: {
          'Authorization': token
        }
      });
      const data = await response.json();
      
      if (data.status === 'error') {
        setManagers([]);
      } else {
        setManagers(data.data);
        setManagerTotalPages(data.total_pages || 1);
        setManagerHasMore(pageNum < data.total_pages);
        setManagerPage(pageNum);
      }
    } catch (error) {
      console.error('관리자 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddManager = async (managerData) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(managerData),
      });

      if (!response.ok) throw new Error('관리자 추가 실패');
      
      await fetchManagers();
      return '관리자가 성공적으로 추가되었습니다.';
    } catch (error) {
      throw new Error('관리자 추가에 실패했습니다.');
    }
  };

  const handleEditManager = async (managerData) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(managerData),
      });

      if (!response.ok) throw new Error('관리자 정보 수정 실패');
      
      await fetchManagers();
      return '관리자 정보가 성공적으로 수정되었습니다.';
    } catch (error) {
      throw new Error('관리자 정보 수정에 실패했습니다.');
    }
  };

  const handleDeleteManager = (userId) => {
    setManagerDeleteConfirm({ isOpen: true, userId });
  };

  const confirmManagerDelete = async () => {
    const { userId } = managerDeleteConfirm;
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) throw new Error('관리자 삭제 실패');
      
      await fetchManagers();
    } catch (error) {
      console.error('관리자 삭제 실패:', error);
    } finally {
      setManagerDeleteConfirm({ isOpen: false, userId: null });
    }
  };

  const handleManagerModalOpen = (mode, manager = null) => {
    setManagerModalMode(mode);
    if (mode === 'edit' && manager) {
      setSelectedManager({
        id: manager.id,
        user_id: manager.user_id,
        user_name: manager.user_name,
        charge_work: manager.charge_work
      });
    }
    setManagerModalOpen(true);
  };

  const handleManagerModalClose = () => {
    setManagerModalOpen(false);
    setSelectedManager(null);
    setManagerModalMode('add');
  };

  const handleManagerPageChange = (newPage) => {
    fetchManagers(newPage);
  };

  // 모바일 페이지네이션을 위한 renderPageNumbers 함수 추가
  const renderPageNumbers = (currentPage, totalPages, onPageChange, pageInputValue, setPageInputValue) => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(start + maxVisible - 1, totalPages);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pageNumbers.push(
        <button
          key={1}
          onClick={() => onPageChange(1)}
          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50"
        >
          1
        </button>
      );
      if (start > 2) {
        pageNumbers.push(<span key="ellipsis1" className="px-1">...</span>);
      }
    }

    for (let i = start; i <= end; i++) {
      if (currentPage === i) {
        // 현재 페이지 - 인풋 박스로 렌더링
        const inputWidth = Math.max(2, String(pageInputValue).length * 0.6 + 1);
        pageNumbers.push(
          <input
            key={i}
            type="text"
            value={pageInputValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setPageInputValue(value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newPage = parseInt(pageInputValue);
                if (newPage >= 1 && newPage <= totalPages) {
                  e.target.blur();
                  setTimeout(() => {
                    onPageChange(newPage);
                  }, 100);
                } else {
                  setPageInputValue(String(currentPage));
                }
              }
            }}
            onMouseUp={(e) => e.preventDefault()}
            onFocus={(e) => e.target.select()}
            onBlur={() => setPageInputValue(String(currentPage))}
            className="flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold"
            style={{ width: `${inputWidth}rem`, minWidth: '2rem', height: '2rem', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 4px' }}
          />
        );
      } else {
        pageNumbers.push(
          <button
            key={i}
            onClick={() => onPageChange(i)}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50"
          >
            {i}
          </button>
        );
      }
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2" className="px-1">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => onPageChange(totalPages)}
          className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };

  // 탭 변경 핸들러
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'teachers' && teachers.length === 0) {
      fetchTeachers();
    } else if (tab === 'managers' && managers.length === 0) {
      fetchManagers();
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    if (activeTab === 'teachers') {
      fetchTeachers();
    } else if (activeTab === 'managers') {
      fetchManagers();
    }
  }, [activeTab]);

  // page 변경 시 pageInputValue 동기화
  useEffect(() => {
    setTeacherPageInputValue(String(teacherPage));
  }, [teacherPage]);

  useEffect(() => {
    setManagerPageInputValue(String(managerPage));
  }, [managerPage]);

  // 화면 크기 변경 시 per_page 재계산 (데스크톱만, 디바운싱 적용)
  useEffect(() => {
    // 모바일에서는 resize 이벤트 무시
    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    let resizeTimer;
    const handleResize = () => {
      // 모바일로 전환되면 무시
      const currentWidth = window.innerWidth;
      if (currentWidth < 768) return;
      
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // 화면 크기가 변경되면 데이터를 다시 불러옴
        if (activeTab === 'teachers' && teachers.length > 0 && !isLoading) {
          fetchTeachers(teacherPage);
        } else if (activeTab === 'managers' && managers.length > 0 && !isLoading) {
          fetchManagers(managerPage);
        }
      }, 500); // 500ms 디바운싱
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTab, teacherPage, managerPage, teachers.length, managers.length, isLoading]);

  return (
    <BypassLayout
      loading={isLoading}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col ${isMobileView ? 'gap-4' : ''} ${
            isMobileView 
              ? 'h-full overflow-y-auto' 
              : 'h-[calc(100vh-2rem-50px)]'
          }`}
          style={isMobileView ? getSafeAreaStyle() : {}}
        >
          {/* 네비게이션 (데스크톱만) */}
          {!isMobileView && (
            <nav className="shrink-0">
            </nav>
          )}


          {/* 메인 카드 컨테이너 */}
          <div className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1' : 'flex-1'
          }`}>
            {/* 탭 헤더 */}
            <div className="shrink-0 border-b">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex w-full">
                  <button
                    onClick={() => handleTabChange('teachers')}
                    className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                      activeTab === 'teachers'
                        ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                        : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                    }`}
                  >
                    <Users className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                    강사 관리
                  </button>
                  <button
                    onClick={() => handleTabChange('managers')}
                    className={`flex-1 rounded-3xl px-4 py-3 transition-colors focus:outline-none lg:px-6 ${
                      activeTab === 'managers'
                        ? `bg-primary font-bold text-white ${isMobileView ? 'text-sm' : 'text-base'}`
                        : `font-medium text-gray-700 hover:bg-primary hover:text-white ${isMobileView ? 'text-xs' : 'text-sm'}`
                    }`}
                  >
                    <UserCheck className="mr-1 inline size-3 lg:mr-2 lg:size-4" />
                    관리자 관리
                  </button>
                </div>

              </div>
            </div>

            {/* 강사 관리 탭 */}
            {activeTab === 'teachers' && (
              <div className="flex w-full flex-1 flex-col overflow-hidden">
                {/* 헤더 영역 */}
                <div className="shrink-0 border-b bg-white">
                  <div style={{ padding: isMobileView ? '12px' : '16px' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">강사 목록</h3>
                        <p className="text-xs text-gray-500">
                          - 총 <span className="font-semibold text-primary">{teachers?.length || 0}</span>개 표시 중
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => handleTeacherModalOpen('add')}
                        className="whitespace-nowrap bg-headerBg text-xs hover:bg-gray-700"
                      >
                        <div className="flex items-center">
                          <Users className="mr-1 size-3" />
                          강사 추가
                        </div>
                      </Button>
                    </div>
                    

                  </div>
                </div>

                {/* 컨텐츠 영역 */}
                <div className="flex-1 flex flex-col overflow-hidden" style={{ padding: isMobileView ? '12px' : '16px' }}>
                  <div className="flex-1 overflow-y-auto">
                  {/* 강사 테이블 */}
                  {isLoading ? (
                    <div className="py-4 text-center">로딩 중...</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[60px]">ID</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[60px]">이름</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[80px]">담당 업무</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[70px]">등록일</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[70px]">수정일</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[60px]">관리</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {teachers.map((teacher) => (
                            <tr 
                              key={teacher.user_id} 
                              className="border-b border-gray-200 hover:bg-primary/10 hover:ring-1 hover:ring-primary/20 md:hover:ring-0 cursor-pointer transition-colors duration-150"
                              style={{
                                borderRadius: '1.5rem'
                              }}
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150 rounded-l-3xl">
                                <span className="text-sm text-gray-900">{teacher.user_id}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-sm font-medium text-gray-900">{teacher.user_name}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-sm text-gray-900">{teacher.charge_work}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-xs text-gray-700">{new Date(teacher.created_at).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-xs text-gray-700">{new Date(teacher.updated_at).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150 rounded-r-3xl">
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleTeacherModalOpen('edit', teacher)}
                                    className="p-1 text-primary hover:text-primary-dark"
                                  >
                                    <PenSquare className="size-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTeacher(teacher.user_id)}
                                    className="p-1 text-danger hover:text-red-700"
                                  >
                                    <Trash2 className="size-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!isLoading && teachers.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                      등록된 강사가 없습니다.
                    </div>
                  )}
                  </div>
                  
                  {/* 페이지네이션 */}
                  {!isLoading && teachers.length > 0 && (
                    <div className="shrink-0 pt-2">
                      {/* 모바일 페이지네이션 */}
                      {isMobileView ? (
                        <div className="flex justify-center">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleTeacherPageChange(teacherPage - 1)}
                              disabled={teacherPage === 1}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              &lt;
                            </button>
                            {renderPageNumbers(teacherPage, teacherTotalPages, handleTeacherPageChange, teacherPageInputValue, setTeacherPageInputValue)}
                            <button
                              onClick={() => handleTeacherPageChange(teacherPage + 1)}
                              disabled={!teacherHasMore}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              &gt;
                            </button>
                          </div>
                        </div>
                      ) : (
                        <DesktopPagination
                          currentPage={teacherPage}
                          totalPages={teacherTotalPages}
                          onPageChange={handleTeacherPageChange}
                          itemsPerPage={calculateItemsPerPage()}
                          totalItems={teachers.length * teacherTotalPages}
                          showInfo={true}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 관리자 관리 탭 */}
            {activeTab === 'managers' && (
              <div className="flex w-full flex-1 flex-col overflow-hidden">
                {/* 헤더 영역 */}
                <div className="shrink-0 border-b bg-white">
                  <div style={{ padding: isMobileView ? '12px' : '16px' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">관리자 목록</h3>
                        <p className="text-xs text-gray-500">
                          - 총 <span className="font-semibold text-primary">{managers?.length || 0}</span>개 표시 중
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => handleManagerModalOpen('add')}
                        className="whitespace-nowrap bg-headerBg text-xs hover:bg-gray-700"
                      >
                        <div className="flex items-center">
                          <UserCheck className="mr-1 size-3" />
                          관리자 추가
                        </div>
                      </Button>
                    </div>
                    

                  </div>
                </div>

                {/* 컨텐츠 영역 */}
                <div className="flex-1 flex flex-col overflow-hidden" style={{ padding: isMobileView ? '12px' : '16px' }}>
                  <div className="flex-1 overflow-y-auto">
                  {/* 관리자 테이블 */}
                  {isLoading ? (
                    <div className="py-4 text-center">로딩 중...</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[60px]">ID</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[60px]">이름</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[80px]">담당 업무</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[70px]">등록일</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[70px]">수정일</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[60px]">관리</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {managers.map((manager) => (
                            <tr 
                              key={manager.id} 
                              className="border-b border-gray-200 hover:bg-primary/10 hover:ring-1 hover:ring-primary/20 md:hover:ring-0 cursor-pointer transition-colors duration-150"
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150 rounded-l-3xl">
                                <span className="text-sm text-gray-900">{manager.user_id}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-sm font-medium text-gray-900">{manager.user_name}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-sm text-gray-900">{manager.charge_work}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-xs text-gray-700">{new Date(manager.created_at).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150">
                                <span className="text-xs text-gray-700">{new Date(manager.updated_at).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150 rounded-r-3xl">
                                <div className="flex gap-1 justify-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleManagerModalOpen('edit', manager)}
                                    className="p-1 text-primary hover:text-primary-dark"
                                  >
                                    <PenSquare className="size-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteManager(manager.user_id)}
                                    className="p-1 text-danger hover:text-red-700"
                                  >
                                    <Trash2 className="size-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!isLoading && managers.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                      등록된 관리자가 없습니다.
                    </div>
                  )}
                  </div>
                  
                  {/* 페이지네이션 */}
                  {!isLoading && managers.length > 0 && (
                    <div className="shrink-0 pt-2">
                      {/* 모바일 페이지네이션 */}
                      {isMobileView ? (
                        <div className="flex justify-center">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleManagerPageChange(managerPage - 1)}
                              disabled={managerPage === 1}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              &lt;
                            </button>
                            {renderPageNumbers(managerPage, managerTotalPages, handleManagerPageChange, managerPageInputValue, setManagerPageInputValue)}
                            <button
                              onClick={() => handleManagerPageChange(managerPage + 1)}
                              disabled={!managerHasMore}
                              className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              &gt;
                            </button>
                          </div>
                        </div>
                      ) : (
                        <DesktopPagination
                          currentPage={managerPage}
                          totalPages={managerTotalPages}
                          onPageChange={handleManagerPageChange}
                          itemsPerPage={calculateItemsPerPage()}
                          totalItems={managers.length * managerTotalPages}
                          showInfo={true}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 모달들 */}
          {/* 강사 모달 */}
          <TeacherModal
            isOpen={teacherModalOpen}
            onClose={handleTeacherModalClose}
            onSubmit={teacherModalMode === 'add' ? handleAddTeacher : handleEditTeacher}
            initialData={selectedTeacher}
            mode={teacherModalMode}
          />

          {/* 관리자 모달 */}
          <ManagerModal
            isOpen={managerModalOpen}
            onClose={handleManagerModalClose}
            onSubmit={managerModalMode === 'add' ? handleAddManager : handleEditManager}
            initialData={selectedManager}
            mode={managerModalMode}
          />

          {/* 강사 삭제 확인 모달 */}
          <ConfirmModal
            isOpen={teacherDeleteConfirm.isOpen}
            onClose={() => setTeacherDeleteConfirm({ isOpen: false, userId: null })}
            onConfirm={confirmTeacherDelete}
            title="강사 삭제"
            message="정말로 이 강사를 삭제하시겠습니까?"
          />

          {/* 관리자 삭제 확인 모달 */}
          <ConfirmModal
            isOpen={managerDeleteConfirm.isOpen}
            onClose={() => setManagerDeleteConfirm({ isOpen: false, userId: null })}
            onConfirm={confirmManagerDelete}
            title="관리자 삭제"
            message="정말로 이 관리자를 삭제하시겠습니까?"
          />
        </div>
      )}
    />
  );
}

export default UserManagement; 